import axios from "axios";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import Top from "../components/Top";
import Navbar from "../components/Navbar";

const CreateUser = ({ currentUser }) => {
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const onSubmit = async (data) => {
        try {
            setLoading((prev) => !prev);
            await axios.post(
                `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/api/v1/user/create`,
                data,
                { withCredentials: true }
            );

            toast.success("User account created successfully.");
            setLoading((prev) => !prev);
            navigate("/users");
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.data) {
                toast.error(error.response.data.message);
            }
        }
    };

    return (
        <div className="py-6 px-3 xl:px-8 sm:px-5">
            <Top currentUser={currentUser} />
            <Navbar currentUser={currentUser} />
            <div className="bg-white rounded-lg px-0 xl:px-8 sm:px-6 p-8 mt-0 w-full sm:w-[450px] xl:w-[520px] mx-auto">
                <h2 className=" font-semibold text-[24px] sm:text-[28px] xl:text-[32px] 2xl:text-[36px] xxl:text-4xl leading-none tracking-normal text-[#5D0E23]">
                    Create User
                </h2>

                <p className="font-medium pt-2 sm:w-[430px] xl:w-[500px] mx-auto text-[12px] sm:text-[13px] xl:text-[14px] 2xl:text-[15px] xxl:text-[16px] tracking-[0%] ">
                    Create a new user and assign roles. Manage access and securely register accounts within the system.
                </p>

                <form
                    className="mt-2 sm:mt-3 2xl:mt-4 flex flex-col gap-2"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className="mb-2">
                        <input
                            autoComplete="new-password"
                            type="text"
                            id="name"
                            maxLength={56}
                            {...register("name", {
                                required: "Name is required", maxLength: {
                                    value: 56,
                                    message: "Name cannot exceed 56 characters"
                                }
                            })}
                            className={`text-[12px] sm:text-[14px] xl:text-[16px] appearance-none border border-[#797979] rounded w-full py-3 px-5 text-[#47474A] leading-tight focus:outline-none  ${errors.name ? "border-red-500" : ""
                                }`}
                            placeholder="Enter name"
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs italic">
                                {errors.name.message}
                            </p>
                        )}
                    </div>

                    <div className="mb-2">
                        <input
                            autoComplete="new-password"
                            type="email"
                            id="email"
                            {...register("email", { required: "Email is required" })}
                            className={`text-[12px] sm:text-[14px] xl:text-[16px] appearance-none border border-[#797979] text-[#47474A] rounded w-full py-3 px-5 leading-tight focus:outline-none  ${errors.email ? "border-red-500" : ""
                                }`}
                            placeholder="Enter email"
                        />
                        {errors.email && (
                            <p className="text-red-500 text-xs italic">
                                {errors.email.message}
                            </p>
                        )}
                    </div>

                    <div className="mb-2 text-[#47474A]">
                        <select
                            id="role"
                            {...register("role", { required: "Role is required" })}
                            className={`text-[12px] sm:text-[14px] xl:text-[16px] appearance-none border cursor-pointer border-[#797979] rounded w-full py-3 px-5 text-[#47474A] leading-tight focus:outline-none  ${errors.role ? "border-red-500" : ""
                                }`}
                        >
                            <option className="text-[#47474A]" value="" disabled selected>
                                Select role
                            </option>
                            <option value="admin">Admin</option>
                            <option value="recruiter">Recruiter</option>
                        </select>
                        {errors.role && (
                            <p className="text-red-500 text-xs italic">
                                {errors.role.message}
                            </p>
                        )}
                    </div>

                    <div className="mb-0">
                        <input
                            autoComplete="new-password"
                            type="password"
                            id="password"
                            {...register("password", {
                                required: "Password is required", pattern: {
                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                                    message:
                                        "Password must be 8+ characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character",
                                },
                            })}
                            className={`text-[12px] sm:text-[14px] xl:text-[16px] appearance-none border border-[#797979] rounded w-full py-3 px-5 text-[#47474A] leading-tight focus:outline-none  ${errors.password ? "border-red-500" : ""
                                }`}
                            placeholder="Enter password"
                        />
                        {errors.password ?
                            <p className="text-red-500 text-xs italic">
                                {errors.password.message}
                            </p> : <p className="text-xs italic mt-1">
                                Password must be 8+ characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character
                            </p>
                        }
                    </div>

                    <div className="flex items-center justify-center">
                        <button
                            type="submit"
                            className="cursor-pointer  font-semibold text-[20px] xl:text-[18px] rounded-[6px] xl:rounded-[6px] mt-5 2xl:mt-6 xxl:mt-7 2xl:rounded-[8px] xxl:rounded-[10px] 2xl:text-[20px] xxl:text-2xl leading-none tracking-normal bg-[#7F132F] w-[55%] hover:bg-[#55051A] text-white py-3 focus:outline-none "
                        >
                            {loading ? (
                                <ClipLoader size={"1em"} color="white" />
                            ) : (
                                "Create User"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateUser;
